import appManager from '../../app/appManager';
import { AuthEvents } from '@modules/AuthModule/types';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import AppModule from '../../app/lib/AppModule';
import {
  CallEvent,
  CallRecordingStatusExtended,
  LegacyJwtFetchStatus,
  OutboundCallerIdInput,
  PcsConversationFetchedEvent,
  SoftPhoneCallControl,
  SoftPhoneDialer,
} from './types';
import { EventBus } from '@servicetitan/web-components';
import WebPhoneService from './WebPhoneService';
import { phoneState, phoneStateHasCallEstablished } from './state';
import ToastModule from '@modules/ToastModule';
import { ITask, TaskState } from '@modules/TasksModule/types';
import {
  ServiceSessionUserEvent,
  ServiceSessionEventType,
  ServiceSessionEvent,
} from '@modules/ServiceModule/workspace/types/ServiceEvents';
import handleRemoveCallAccepting from './events/handleRemoveCallAccepting';
import handleTaskAccept from './events/handleTaskAccept';
import AuthModule from '@modules/AuthModule';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import handlePcsConversationFetched from './events/handlePcsConversationFetched';
import { CallRecording } from '@api/generated/graphql';
import getRecordingState from './lib/getRecordingState';
import FullStoryModule from '@modules/FullStoryModule';
import strings from './constants/strings';
import TasksModule from '@modules/TasksModule';
import { getConversationBySidQuery } from './lib/getConversationBySid';
import { getClient } from '@api/client';

const RECONNECTION_TIMEOUT_MS = 10000; // 10 seconds timeout

export class SoftPhoneModule extends AppModule {
  private _allowUI = false;
  private _initialized = false;
  private _reconnectionResolve: (() => void) | null = null;
  private _reconnectionPromise: Promise<void> | null = null;
  override events = [
    'phone.call.answered',
    'phone.call.ended',
    'phone.call.missed',
    'phone.call.placed',
    'phone.prepare.outbound',
    'phone.call.failed',
    'phone.pcs.conversation.fetched',
    'phone.registered',
  ];

  override preload(): void {
    // keep track of signed in status so we
    // don't show UI when not signed in
    this.app.events.on(AuthEvents.SIGNIN, () => {
      this._allowUI = true;
      phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Fetching;
      void AuthModule.getNewWebPhoneToken()
        .then(() => {
          phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Idle;
        })
        .catch(() => {
          phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Failed;
        });

      // register handlers for signout to prevent user from signing out
      // while a call is in progress
      AuthModule.registerCanSignoutHandler('softphone', async () => {
        if (this.isAttemptingCall()) {
          DiagnosticsModule.reportUserError(
            'You cannot sign out because a call is in progress',
            {
              priority: 'passive',
            },
          );
          return false;
        }
        return true;
      });
    });
    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this._allowUI = false;
      this.disconnectSoftPhone();
      phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Idle;

      // unregister handlers for signout
      AuthModule.unregisterCanSignoutHandler('softphone');
    });

    // task handlers
    this.app.events.on('task.action.accept', (task: ITask) =>
      handleTaskAccept(task),
    );
    this.app.events.on('task.action.reject', (task: ITask) =>
      handleRemoveCallAccepting(task.id),
    );

    // service session handlers
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.DELIVERED,
      (ev) => handleRemoveCallAccepting(ev.sessionSid),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.REJECTED,
      (ev) => handleRemoveCallAccepting(ev.sessionSid),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.RESCINDED,
      (ev) => handleRemoveCallAccepting(ev.sessionSid),
    );
    this.app.events.on<ServiceSessionEvent>(
      ServiceSessionEventType.DELETED,
      (ev) => handleRemoveCallAccepting(ev.sessionSid),
    );

    // phone call handlers
    this.app.events.on<CallEvent>('phone.call.answered', (ev) =>
      handleRemoveCallAccepting(ev.pcsConversationSid || ev.callId),
    );
    this.app.events.on<CallEvent>('phone.call.missed', (ev) =>
      handleRemoveCallAccepting(ev.pcsConversationSid || ev.callId),
    );

    this.app.events.on<PcsConversationFetchedEvent>(
      'phone.pcs.conversation.fetched',
      (ev) => {
        handlePcsConversationFetched(
          ev.pcsConversation,
          ev.phoneStateUpdateEvent,
          this.app.logger,
        );
      },
    );

    this.app.events.on('phone.registered', () => {
      if (this._reconnectionResolve) {
        this._reconnectionResolve();
        this._reconnectionResolve = null;
        this._reconnectionPromise = null;
      }
    });

    // Wait for feature flags to be initialized before setting up diagnostics collection
    this.app.events.on('featureflags.initialized', () => {
      // collect diagnostics periodically, if the `feature-log-softphone-connection-info-interval` feature flag is enabled and has a numeric value (in seconds), if the flag is OFF, the value will be 0
      const { value: diagnosticsIntervalSeconds } =
        FeatureFlagModule.getFeatureFlagValue(
          FeatureFlagKeys.featureLogSoftphoneConnectionInfoInterval,
        );
      if (
        typeof diagnosticsIntervalSeconds === 'number' &&
        diagnosticsIntervalSeconds > 0
      ) {
        const diagnosticsIntervalMs = diagnosticsIntervalSeconds * 1000;
        setInterval(() => {
          if (!this._initialized) {
            return;
          }
          WebPhoneService.reportConnectionInfo();
        }, diagnosticsIntervalMs);
      }
    });
  }

  public get enabled(): boolean {
    // TODO: check if MFE softphone enabled
    return false;
  }

  public get dialer(): SoftPhoneDialer {
    this.isPhoneInitialized({ showToast: true });
    return {
      dial: (number: string | number) =>
        WebPhoneService.dial(number.toString()),
      clear: () => WebPhoneService.clear(),
      backspace: () => WebPhoneService.backspace(),
      autoAction: () => WebPhoneService.autoAction(),
    };
  }

  public get callControl(): SoftPhoneCallControl {
    this.isPhoneInitialized({ showToast: true });
    return {
      answer: (callId?: string) => WebPhoneService.answer(callId),
      cancel: (callId?: string) => WebPhoneService.cancel(callId),
      hangup: (callId?: string) => WebPhoneService.hangup(callId),
      hold: () => WebPhoneService.hold(),
      unhold: (callId?: string) => WebPhoneService.unhold(callId),
      mute: () => WebPhoneService.mute(),
      unmute: () => WebPhoneService.unmute(),
      transfer: (number: string) => WebPhoneService.transfer(number),
    };
  }

  public isAttemptingCall(): boolean {
    return Object.values(phoneState.callList).some((call) => call.hasSession);
  }

  public isInCall(): boolean {
    return phoneStateHasCallEstablished();
  }

  public isPhoneConnected(): boolean {
    return phoneState.phoneConnected;
  }

  public endCall(callId: string): void {
    const call = phoneState.callList.find((c) => c.callId === callId);
    if (call && call.hasSession) {
      WebPhoneService.hangup(callId);
    }
  }

  /**
   * Clears the soft phone service and sets the initialized flag to false
   * Good for cleaning up {@link initializeWebPhoneService} when the MFE is unmounted
   */
  public uninitializeWebPhoneService() {
    WebPhoneService.uninitialize();
    this._initialized = false;
  }

  /**
   * Initializes the soft phone service with the specified event bus.
   * Should be cleaned up with {@link uninitializeWebPhoneService}.
   * @param webPhoneEventBus the event bus to use for communication with the MFE
   */
  public initializeWebPhoneService(webPhoneEventBus?: EventBus) {
    if (!webPhoneEventBus) {
      throw new Error('Event bus is required to initialize the softphone');
    }
    if (this._initialized) {
      return;
    }

    WebPhoneService.initialize(webPhoneEventBus, this.app.logger);
    this._initialized = true;
  }

  public prepareOutboundCall(
    outgoingNumber: string,
    displayName?: string,
    tenantId?: string | null,
    taskAttributes?: Record<string, any>,
  ) {
    this.app.events.emit('phone.prepare.outbound', {
      outgoingNumber,
      displayName,
      tenantId,
      taskAttributes,
    });
  }

  public callNumber(
    number: string,
    displayName?: string,
    tenantId?: string | null,
    taskAttributes?: Record<string, any>,
  ) {
    if (this.isPhoneInitialized({ showToast: true })) {
      this.prepareOutboundCall(number, displayName, tenantId, taskAttributes);
      WebPhoneService.callNumber(number);
    }
  }

  /**
   * Places an outbound call without creating a task.
   * Used by Speed to Lead where the task already exists.
   */
  public placeOutboundCall(
    number: string,
    outboundCallerId?: OutboundCallerIdInput,
  ) {
    if (this.isPhoneInitialized({ showToast: true })) {
      if (outboundCallerId) {
        return WebPhoneService.callNumberWithCallId(number, outboundCallerId);
      } else {
        WebPhoneService.callNumber(number);
      }
    }
  }

  public callNumberWithCallId(
    number: string,
    outboundCallerId: OutboundCallerIdInput,
    displayName?: string,
    tenantId?: string | null,
    taskAttributes?: Record<string, any>,
  ) {
    if (this.isPhoneInitialized({ showToast: true })) {
      this.prepareOutboundCall(number, displayName, tenantId, taskAttributes);
      return WebPhoneService.callNumberWithCallId(number, outboundCallerId);
    }
  }

  public transferNumber(number: string) {
    if (this.isPhoneInitialized({ showToast: true })) {
      WebPhoneService.transfer(number);
    }
  }

  private isPhoneInitialized(options?: { showToast?: boolean }): boolean {
    if (this._allowUI && !this._initialized && options?.showToast) {
      ToastModule.showToast({
        title: 'Softphone not initialized',
        status: 'critical',
      });
    }
    return this._initialized;
  }

  public setRecordingStatus(
    callId: string,
    status: CallRecordingStatusExtended,
  ) {
    if (phoneState.callAttributes[callId]) {
      phoneState.callAttributes[callId].recordingState.status = status;
    }
  }

  public disconnectSoftPhone(disconnectTriggeredLocally: boolean = true) {
    phoneState.disconnectTriggeredLocally = disconnectTriggeredLocally;
    WebPhoneService.disconnect();
  }

  public setRecordingState(callId: string, recordingStatus: CallRecording) {
    if (phoneState.callAttributes[callId]) {
      phoneState.callAttributes[callId].recordingState =
        getRecordingState(recordingStatus);
    }
  }

  public collectWebPhoneDiagnostics() {
    return WebPhoneService.collectDiagnostics();
  }

  public async reconnect(): Promise<void> {
    // Return existing promise if reconnection is in progress
    if (this._reconnectionPromise) {
      return this._reconnectionPromise;
    }

    FullStoryModule.trackEvent('[CCP-Call] Phone Reconnection Started', {});
    // Get new token first
    const newToken = await AuthModule.getNewWebPhoneToken();

    // Create new reconnection promise
    this._reconnectionPromise = new Promise<void>((resolve, reject) => {
      const newResolve = () => {
        clearTimeout(timeoutId);
        resolve();
      };
      const timeoutId = setTimeout(() => {
        if (this._reconnectionResolve === newResolve) {
          FullStoryModule.trackEvent(
            '[CCP-Call] Phone Reconnection Timed Out',
            {},
          );
          this._reconnectionResolve = null;
          this._reconnectionPromise = null;
          reject(
            new Error(
              'Reconnection timeout - phone registration did not occur',
            ),
          );
        }
      }, RECONNECTION_TIMEOUT_MS);

      WebPhoneService.reconnect(newToken);

      this._reconnectionResolve = newResolve;
    });

    return this._reconnectionPromise;
  }

  public activateWebPhone() {
    if (phoneState.phoneConnected) {
      this.app.logger.warn(
        'WebPhone is already connected, no need to activate again',
      );
      return;
    }

    // Since the MFE will not receive the call retroactively, we do not allow the user to activate the webphone while being offered a call.
    const tasks = TasksModule.getTasks();
    const isAnyTaskInOfferedState = tasks.some(
      (task) => task.state === TaskState.Offered,
    );

    if (isAnyTaskInOfferedState) {
      ToastModule.showToast({
        title: strings.toasts.activateWhileOfferedCall,
        status: 'critical',
      });
      return;
    }
    WebPhoneService.overrideRegistration();
    phoneState.registrationOverwritten = false;
  }

  public retryLegacyJwtFetch(): void {
    if (phoneState.legacyJwtFetchStatus === LegacyJwtFetchStatus.Fetching) {
      return;
    }
    phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Fetching;
    void AuthModule.getNewWebPhoneToken()
      .then(() => {
        phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Idle;
      })
      .catch(() => {
        phoneState.legacyJwtFetchStatus = LegacyJwtFetchStatus.Failed;
      });
  }

  /**
   * Fetches conversation data by session ID
   * @param sid The conversation session ID
   * @returns Promise with the conversation data
   */
  public async getConversationBySid(sid: string) {
    return getClient().query({
      query: getConversationBySidQuery,
      variables: { sid },
      fetchPolicy: 'network-only',
    });
  }
}

export default new SoftPhoneModule(
  appManager.events,
  appManager.logger('SoftPhone'),
);

export { audioDevicesState } from './audioDevicesState';
export { useAudioDevices } from './useAudioDevices';
